import { useEffect } from 'react';
import { useKioskStore } from '@/store/kiosk-store';

const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * Polls /health every 30s and listens to browser online/offline events
 * to keep the isOnline flag in the store accurate.
 */
export function useHeartbeat(): void {
  const setOnline = useKioskStore((s) => s.setOnline);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const base = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '';
        const url = base ? `${base.replace(/\/$/, '')}/api/v1/health` : '/api/v1/health';
        const res = await fetch(url, { method: 'GET' });
        setOnline(res.ok);
      } catch {
        setOnline(false);
      }
    };

    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    void checkHealth();

    const interval = setInterval(() => void checkHealth(), HEARTBEAT_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [setOnline]);
}
